'use client';

import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  AppBar,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Podium } from '@prismo-io/charts';
import { ReadingKeyModal } from '@prismo-io/design-system';
import {
  type FC,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import type { PodiumsDataT } from '../../../../../utils/fetch-quark-podiums';
import { ReadingKey } from '../reading-key';

type Props = {
  podiumsData: PodiumsDataT;
};

function a11yProps(index: number) {
  return {
    id: `podium-tab-${index}`,
    'aria-controls': `podium-tabpanel-${index}`,
  };
}

const favoriteToScore: Record<number, number> = {
  [0]: 0,
  [1]: 300,
  [2]: 200,
  [3]: 100,
};

export const QuarkPodiumsCard: FC<Props> = (props) => {
  const { podiumsData } = props;

  const { i18n } = useLingui();

  const titlesList = useMemo(
    () => [
      t`Principales compétences`,
      t`Principales expériences`,
      t`Principales formations`,
    ],
    [i18n.locale]
  );

  const undefinedItems = useMemo(
    () => new Array(3).fill(t`Non définie`),
    [i18n.locale]
  );

  const skillList = useMemo(
    () =>
      [...podiumsData.skills, ...podiumsData.knowledges]
        .map((skill) => {
          const { level = 0, interest = 0, favorite = 0 } = skill;

          const score =
            (level ?? 0) + (interest ?? 0) + favoriteToScore[favorite ?? 0];

          return {
            id: skill.ref.code,
            name: skill.ref.name,
            favorite: skill.favorite,
            score,
          };
        })
        .sort((a, b) => {
          if (a.score === b.score) {
            return a.name.localeCompare(b.name);
          }

          return b.score - a.score;
        })
        .slice(0, 3)
        .map((skill) => skill.name),
    [podiumsData.skills, podiumsData.knowledges]
  );

  const expList = useMemo(
    () =>
      podiumsData.experiences
        .map((exp) => {
          const { favorite = 0 } = exp;

          const score = favoriteToScore[favorite ?? 0];

          return {
            ...exp,
            name: exp.designation?.name ?? exp.custom_name ?? t`Non définie`,
            score,
          };
        })
        .sort((a, b) => {
          if (a.score === b.score) {
            return a.name.localeCompare(b.name);
          }

          return b.score - a.score;
        })
        .slice(0, 3)
        .map((exp) => exp.name),
    [podiumsData.experiences, i18n.locale]
  );

  const formList = useMemo(
    () =>
      podiumsData.formations
        .map((form) => {
          const { favorite = 0 } = form;

          const score = favoriteToScore[favorite ?? 0];

          return {
            ...form,
            name: form.name,
            score,
          };
        })
        .sort((a, b) => {
          if (a.score === b.score) {
            return a.name.localeCompare(b.name);
          }

          return b.score - a.score;
        })
        .slice(0, 3)
        .map((form) => form.name),
    [podiumsData.formations]
  );

  const skillsPodiumData = useMemo(
    () => [...skillList, ...undefinedItems].slice(0, 3),
    [skillList, undefinedItems]
  );

  const expsPodiumData = useMemo(
    () => [...expList, ...undefinedItems].slice(0, 3),
    [expList, undefinedItems]
  );

  const formsPodiumData = useMemo(
    () => [...formList, ...undefinedItems].slice(0, 3),
    [formList, undefinedItems]
  );

  const formatedPodiumsData = useMemo(
    () => [skillsPodiumData, expsPodiumData, formsPodiumData],
    [skillsPodiumData, expsPodiumData, formsPodiumData]
  );

  const [tabIdx, setTabIdx] = useState(0);
  const visiblePodiumData = useMemo(
    () => formatedPodiumsData[tabIdx],
    [formatedPodiumsData, tabIdx]
  );

  const visibleTitle = useMemo(() => titlesList[tabIdx], [titlesList, tabIdx]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: number) => {
      setTabIdx(newValue);
    },
    [setTabIdx]
  );

  return (
    <Stack spacing={3} component="article">
      <Stack direction="row" spacing={2} alignItems="center" component="header">
        <Typography variant="h3">{visibleTitle}</Typography>
        <ReadingKeyModal>
          <ReadingKey />
        </ReadingKeyModal>
      </Stack>
      <Stack direction="row" justifyContent="center">
        <Container maxWidth="xs">
          <Paper elevation={3}>
            <Stack direction="column" alignItems="center" paddingTop={3}>
              <Stack width="100%" flexDirection="row" justifyContent="center">
                <Podium
                  itemName1={visiblePodiumData[0]}
                  itemName2={visiblePodiumData[1]}
                  itemName3={visiblePodiumData[2]}
                />
              </Stack>
              <AppBar position="static">
                <Tabs
                  value={tabIdx}
                  onChange={handleChange}
                  variant="fullWidth"
                  aria-label="podiums-tabs"
                >
                  <Tab
                    label={t`Compétences`}
                    {...a11yProps(0)}
                    sx={{
                      backgroundColor: 'white',
                      borderTop: '2px solid #f0f0f0',
                      borderRight: '1px solid #f0f0f0',
                    }}
                  />
                  <Tab
                    label={t`Expériences`}
                    {...a11yProps(1)}
                    sx={{
                      backgroundColor: 'white',
                      borderTop: '2px solid #f0f0f0',
                      borderLeft: '1px solid #f0f0f0',
                      borderRight: '1px solid #f0f0f0',
                    }}
                  />
                  <Tab
                    label={t`Formations`}
                    {...a11yProps(2)}
                    sx={{
                      backgroundColor: 'white',
                      borderTop: '2px solid #f0f0f0',
                      borderLeft: '1px solid #f0f0f0',
                    }}
                  />
                </Tabs>
              </AppBar>
            </Stack>
          </Paper>
        </Container>
      </Stack>
    </Stack>
  );
};
